// src/components/modals/SMSLogsModal.jsx
import { useState, useEffect, useCallback } from 'react'
import { X, FileText, MessageCircle, Users, Phone, Clock, RefreshCw, Search, Filter, AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import InlineLoader from '../ui/InlineLoader'
import ButtonSpinner from '../ui/ButtonSpinner'
import SMSCampaignService from '../../services/smsCampaignService'
import { SMSCampaignModel, SMS_LOG_STATUS } from '../../utils/smsCampaignModel'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

const SMSLogsModal = ({ isOpen, onClose }) => {
  // State Management
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)

  // Filter and Search State
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [campaignFilter, setCampaignFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('')

  // Stats State
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    pending: 0
  })

  // Toast notification function
  const showToast = useCallback((message, type) => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }, [])

  // Get unique campaigns for filter
  const [campaigns, setCampaigns] = useState([])

  // Load SMS logs
  const loadSMSLogs = useCallback(async (params = {}) => {
    try {
      console.log('📱 Loading SMS logs with params:', params)
      const response = await SMSCampaignService.getSMSLogs(params)
      
      if (response.success) {
        const parsedData = SMSCampaignService.parseResponse(response)
        
        if (parsedData.logs) {
          const formattedLogs = parsedData.logs.map(log => 
            SMSCampaignModel.formatLogForDisplay(log)
          )
          setLogs(formattedLogs)
          calculateStats(formattedLogs)
          extractUniqueCampaigns(formattedLogs)
        }
      }
    } catch (error) {
      console.error('Failed to load SMS logs:', error)
      throw error
    }
  }, [])

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await loadSMSLogs()
    } catch (error) {
      console.error('Failed to load initial data:', error)
      setError('Failed to load SMS logs')
      showToast('Failed to load SMS logs', 'error')
    } finally {
      setLoading(false)
    }
  }, [loadSMSLogs, showToast])

  useEffect(() => {
    if (isOpen) {
      loadInitialData()
    }
  }, [isOpen, loadInitialData])

  const calculateStats = (logsData) => {
    const stats = logsData.reduce((acc, log) => {
      acc.total++
      acc[log.status] = (acc[log.status] || 0) + 1
      return acc
    }, { total: 0, success: 0, failed: 0, pending: 0 })
    
    setStats(stats)
  }

  const extractUniqueCampaigns = (logsData) => {
    const uniqueCampaigns = []
    const seenCampaigns = new Set()
    
    logsData.forEach(log => {
      if (!seenCampaigns.has(log.campaign.id)) {
        uniqueCampaigns.push({
          id: log.campaign.id,
          title: log.campaign.title,
          status: log.campaign.status
        })
        seenCampaigns.add(log.campaign.id)
      }
    })
    
    setCampaigns(uniqueCampaigns)
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await loadSMSLogs()
      showToast('SMS logs refreshed successfully', 'success')
    } catch (error) {
      console.error('Failed to refresh logs:', error)
      showToast('Failed to refresh logs', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  const handleSearch = async () => {
    const params = {}
    
    if (searchTerm) params.search = searchTerm
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter
    if (campaignFilter && campaignFilter !== 'all') params.campaign = campaignFilter
    if (dateFilter) params.date = dateFilter
    
    try {
      setLoading(true)
      await loadSMSLogs(params)
    } catch {
      showToast('Failed to search logs', 'error')
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setCampaignFilter('all')
    setDateFilter('')
    loadSMSLogs()
  }

  const handleClose = () => {
    // Reset all state when closing
    setLogs([])
    setCampaigns([])
    setSearchTerm('')
    setStatusFilter('all')
    setCampaignFilter('all')
    setDateFilter('')
    setError(null)
    setToast(null)
    onClose()
  }

  // Get filtered logs
  const getFilteredLogs = () => {
    return logs.filter(log => {
      const matchesSearch = !searchTerm || 
        log.campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.customer.mobileNumber.includes(searchTerm)
      
      const matchesStatus = statusFilter === 'all' || log.status === statusFilter
      const matchesCampaign = campaignFilter === 'all' || log.campaign.id === campaignFilter
      
      return matchesSearch && matchesStatus && matchesCampaign
    })
  }

  const filteredLogs = getFilteredLogs()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-full">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">SMS Campaign Logs</h2>
              <p className="text-sm text-gray-600">View detailed SMS delivery logs and status</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="flex items-center gap-2"
            >
              {refreshing ? <ButtonSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="py-8">
              <InlineLoader message="Loading SMS logs..." />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Logs</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                    </div>
                    <div className="p-2 bg-gray-100 rounded-full">
                      <MessageCircle className="h-5 w-5 text-gray-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Success</p>
                      <p className="text-2xl font-bold text-green-900">{stats.success}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-full">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Failed</p>
                      <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded-full">
                      <XCircle className="h-5 w-5 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-yellow-600">Pending</p>
                      <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
                    </div>
                    <div className="p-2 bg-yellow-100 rounded-full">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                {/* Filter Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-5 w-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Filter SMS Logs</h2>
                  </div>
                  {(searchTerm || statusFilter !== 'all' || campaignFilter !== 'all' || dateFilter) && (
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      className="text-orange-500 border-orange-500 hover:bg-orange-50"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>

                {/* Filter Controls */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Search */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input
                      type="text"
                      placeholder="Search logs by campaign, customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11 border-gray-300"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <option value="all">All Status</option>
                      {Object.values(SMS_LOG_STATUS).map(status => (
                        <option key={status} value={status}>
                          {SMSCampaignModel.getLogStatusConfig(status).label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Campaign Filter */}
                  <div>
                    <select
                      value={campaignFilter}
                      onChange={(e) => setCampaignFilter(e.target.value)}
                      className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <option value="all">All Campaigns</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <Input
                      type="date"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="h-11 border-gray-300"
                    />
                  </div>
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Logs Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {filteredLogs.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {logs.length === 0 
                        ? "No SMS logs available yet."
                        : "Try adjusting your search or filter criteria."
                      }
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Campaign
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Mobile Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Attempts
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sent At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Error Message
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {log.campaign.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {log.campaign.id}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Users className="h-4 w-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {log.customer.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    ID: {log.customer.id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Phone className="h-4 w-4 text-gray-400 mr-2" />
                                <div className="text-sm text-gray-900 font-mono">
                                  {log.mobileNumber}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${log.statusConfig.color}`}>
                                <span className="mr-1">{log.statusConfig.icon}</span>
                                {log.statusConfig.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.attempts}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {log.sentAtFormatted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {log.errorMessage ? (
                                <div className="text-sm text-red-600 max-w-xs truncate" title={log.errorMessage}>
                                  {log.errorMessage}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
          <Button
            onClick={handleClose}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            Close
          </Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default SMSLogsModal